import { LoadingIndicator } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import { api, type ChannelPlatformStatus } from "@/lib/api";
import {
  choiceFromCurrent,
  defaultChannelPlatforms,
  retryWhileRestarting,
  type DefaultChannelChoice,
} from "@/lib/default-channel";
import { saveDefaultChannelChoice } from "@/pages/container-tabs/save-default-channel";
import {
  DefaultChannelPicker,
  DefaultChannelSaveError,
} from "@/pages/container-tabs/DefaultChannelPicker";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

// Connecting Slack restarts the Boo; its Slack side can answer a few seconds
// after the rest. Poll the platform report until Slack shows up (~90s).
const SLACK_READY_POLLS = 30;
const slackReady = (platforms: ChannelPlatformStatus[] | undefined) =>
  platforms?.some((p) => p.platform === "slack" && p.enabled) === true;

/**
 * Onboarding: right after Slack connects, the installer picks where Boo posts
 * when nothing names a place. "Just me" (the installer's direct messages) is
 * preselected. Boo's greeting is held until Continue, which saves the choice
 * and releases it. A Boo that predates the default channel skips straight
 * through.
 */
export function DefaultChannelStep({
  containerId,
  botName,
  onDone,
}: {
  containerId: string;
  botName: string;
  onDone: (choice: DefaultChannelChoice) => void;
}) {
  const queryClient = useQueryClient();
  const channelsQuery = useQuery({
    queryKey: ["channels", containerId],
    queryFn: () => api.containers.channels.list(containerId),
    retry: retryWhileRestarting,
    retryDelay: 3000,
  });
  const platformsQuery = useQuery({
    queryKey: ["channel-platforms", containerId],
    queryFn: () => api.containers.channels.platforms(containerId),
    retry: retryWhileRestarting,
    retryDelay: 3000,
    refetchInterval: (q) =>
      slackReady(q.state.data?.platforms) || q.state.dataUpdateCount >= SLACK_READY_POLLS
        ? false
        : 3000,
  });

  const data = channelsQuery.data;
  // No defaultChannel key at all = this Boo predates the default channel.
  const unsupported = data !== undefined && data.defaultChannel === undefined;
  const slackPlatforms = defaultChannelPlatforms(platformsQuery.data?.platforms).filter(
    (p) => p.platform === "slack",
  );
  const platformPolls =
    queryClient.getQueryState(["channel-platforms", containerId])?.dataUpdateCount ?? 0;
  const waitingForSlack =
    !slackReady(platformsQuery.data?.platforms) &&
    platformPolls < SLACK_READY_POLLS &&
    !platformsQuery.isError;

  const [picked, setPicked] = useState<DefaultChannelChoice | null>(null);
  const choice = picked ?? choiceFromCurrent(data?.defaultChannel);

  const finish = useMutation({
    mutationFn: async (next: DefaultChannelChoice) => {
      // Without a channel list (Boo still restarting, or too old) there is
      // nothing to save: "Just me" is already what an unset default means.
      const saved =
        data && !unsupported
          ? await saveDefaultChannelChoice(containerId, next, data.defaultChannel, {
              onCreated: setPicked,
            })
          : next;
      await api.onboarding.kickoff();
      return saved;
    },
    onSuccess: onDone,
  });

  useEffect(() => {
    if (unsupported && finish.isIdle) finish.mutate({ kind: "none" });
  }, [unsupported, finish]);

  // The person on this screen installed Slack, so assume the installer DM
  // until a loaded Boo says it doesn't know them; the wording must not flip
  // while the Boo restarts after connecting Slack.
  const installerKnown = data === undefined || data.fallback != null;

  if (unsupported) return <LoadingIndicator message="Finishing up…" />;

  return (
    // Bottom padding on phones clears the layout's floating Log out button.
    <div className="flex flex-col gap-6 pb-16 md:pb-0">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold text-balance">Where should {botName} check in?</h1>
        <p className="text-sm text-muted-foreground">
          It'll still work in any channel you invite it to.
        </p>
      </div>

      <DefaultChannelPicker
        containerId={containerId}
        platforms={slackPlatforms}
        value={choice}
        onChange={setPicked}
        botName={botName}
        disabled={finish.isPending}
        connecting={waitingForSlack && slackPlatforms.length === 0}
        noneOption={
          installerKnown ? { title: "Just me", detail: "Direct message" } : { title: "Decide later" }
        }
      />

      {finish.isError && <DefaultChannelSaveError error={finish.error} platform="slack" />}

      <div className="flex flex-col gap-2">
        <Button className="w-full" loading={finish.isPending} onClick={() => finish.mutate(choice)}>
          Continue
        </Button>
        <p className="text-center text-xs text-muted-foreground">You can change this anytime.</p>
      </div>
    </div>
  );
}
